/** Date/time/name formatting for screens. Dates are `YYYY-MM-DD`; local datetimes `YYYY-MM-DDTHH:MM`; timestamps ISO UTC. */
import { addDays, daysBetween } from "@shared/dates";
import { todayIso } from "@shared/ids";
import type { BookingStatus } from "@shared/schemas/bookings";
import type { ChipTone } from "@web/theme/theme";

const utc = (date: string) => new Date(`${date}T00:00:00Z`);
const pad = (n: number) => String(n).padStart(2, "0");

/** Today in the company timezone. */
export const todayIn = (tz: string | null | undefined) => todayIso(tz ?? "America/Chicago");

export { addDays, daysBetween };

/** "Sat, May 15, 2027" (weekday + year optional). */
export function fmtDate(date: string | null | undefined, opts: { weekday?: boolean; year?: boolean } = {}): string {
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: opts.year === false ? undefined : "numeric", weekday: opts.weekday ? "short" : undefined }).format(utc(date));
}

/** "May 15" */
export const fmtDay = (date: string | null | undefined) => fmtDate(date, { year: false });

/** "May 15 – May 17, 2027" or a single date. */
export const fmtRange = (from: string, to: string) => (to && to !== from ? `${from.slice(0, 4) === to.slice(0, 4) ? fmtDay(from) : fmtDate(from)} – ${fmtDate(to)}` : fmtDate(from, { weekday: true }));

/** "4:00 PM" from "16:00". */
export function fmtTime(time: string | null | undefined): string {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", hour: "numeric", minute: "2-digit" }).format(new Date(Date.UTC(2000, 0, 1, h, m)));
}

/** "May 15, 4:00 PM" from a local datetime. */
export function fmtLocal(dt: string | null | undefined): string {
  if (!dt) return "—";
  const { date, time } = splitLocal(dt);
  return `${fmtDay(date)}, ${fmtTime(time)}`;
}

/** ISO UTC timestamp → "Sep 17, 2026, 2:05 PM" in the browser zone. */
export const fmtStamp = (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(iso)) : "—");

export const splitLocal = (dt: string | null | undefined) => ({ date: dt ? dt.slice(0, 10) : "", time: dt ? dt.slice(11, 16) : "" });
export const joinLocal = (date: string, time: string) => (date && time ? `${date}T${time}` : null);

export const monthOf = (date: string) => date.slice(0, 7);
export const monthLabel = (month: string) => new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "long", year: "numeric" }).format(utc(`${month}-01`));
export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}
export const monthStart = (month: string) => `${month}-01`;
export const monthEnd = (month: string) => addDays(monthStart(shiftMonth(month, 1)), -1);

export const fullName = (c: { firstName?: string | null; lastName?: string | null } | null | undefined) => (c ? `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() : "");

export const STATUS_LABEL: Record<BookingStatus, string> = { pending: "Pending", contract_sent: "Contract sent", signed: "Signed", booked: "Booked", completed: "Completed", cancelled: "Cancelled" };
export const STATUS_TONE: Record<BookingStatus, ChipTone> = { pending: "neutral", contract_sent: "accent", signed: "accent", booked: "success", completed: "neutral", cancelled: "danger" };
export const statusLabel = (s: string) => STATUS_LABEL[s as BookingStatus] ?? s;
export const statusTone = (s: string): ChipTone => STATUS_TONE[s as BookingStatus] ?? "neutral";

/** Human label for an activity action ("record_payment" → "Record payment"). */
export const humanize = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** Digits-only phone → "(251) 555-0100" for display. */
export function fmtPhone(p: string | null | undefined): string {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  const n = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : p;
}

export const pctLabel = (pct: string | number) => `${String(pct).replace(/\.0+$/, "")}%`;

/** Row/header title for a booking: company name → client name → event name → "No contact" (migrated rows may lack a contact). */
export function bookingTitle(b: { clientIsCompany?: number | boolean | null; clientCompanyName?: string | null; clientName?: string | null; eventName?: string | null }): string {
  const company = b.clientIsCompany && b.clientCompanyName ? b.clientCompanyName : "";
  return company || (b.clientName ?? "").trim() || (b.clientCompanyName ?? "").trim() || (b.eventName ?? "").trim() || "No contact";
}
